import { add, differenceInMinutes, format, isSameDay } from "date-fns"

export function formatFullDateTime(date: Date | string) {
  date = new Date(date)

  return format(date, "h:mm a, do MMM yyyy")
}

export function formatFullDate(date: Date | string) {
  date = new Date(date)

  return format(date, "do MMM yyyy")
}

export function formatShortDate(date: Date | string) {
  date = new Date(date)

  return format(date, "d/M/yyyy")
}

export function formatDateMonth(date: Date | string) {
  date = new Date(date)

  return format(date, "do MMM")
}

export function formatDateForUrl(date: Date | string) {
  date = new Date(date)

  return format(date, "yyyy-MM-dd")
}

export function clearTime(date: Date | number | string) {
  const cloned = new Date(date)
  cloned.setHours(0, 0, 0, 0)
  return cloned
}

export function differenceInMinutesOfWorkingHours(start: Date, end: Date): number {
  if (start > end) {
    const temp = start
    start = end
    end = temp
  }

  // Helper function to adjust dates outside of working hours
  function adjustDateToWorkingHours(date: Date, isStartDate: boolean): Date {
    const adjustedDate = new Date(date)
    if (date.getHours() < 9) {
      adjustedDate.setHours(9, 0, 0, 0)
    } else if (date.getHours() >= 17 || (date.getHours() === 16 && date.getMinutes() > 0)) {
      adjustedDate.setHours(isStartDate ? 17 : 9, isStartDate ? 0 : 0, 0, 0)
      if (isStartDate) {
        adjustedDate.setDate(adjustedDate.getDate() + 1)
      }
    }
    return adjustedDate
  }

  const adjustedStart = adjustDateToWorkingHours(start, true)
  const adjustedEnd = adjustDateToWorkingHours(end, false)

  let totalMinutes = 0

  while (adjustedStart < adjustedEnd) {
    // If both dates are on the same day
    if (adjustedStart.toDateString() === adjustedEnd.toDateString()) {
      totalMinutes +=
        adjustedEnd.getHours() * 60 +
        adjustedEnd.getMinutes() -
        (adjustedStart.getHours() * 60 + adjustedStart.getMinutes())
      break
    } else {
      totalMinutes += 17 * 60 - (adjustedStart.getHours() * 60 + adjustedStart.getMinutes())
      adjustedStart.setDate(adjustedStart.getDate() + 1)
      adjustedStart.setHours(9, 0, 0, 0)
    }
  }

  return totalMinutes
}

export function formatDurationMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  return `${hours}h ${remainingMinutes}m`
}

export function formatDurationHours(hours: number) {
  return `${Math.round(hours)}h`
}

export function formatDateToTime(date: Date | string) {
  date = new Date(date)

  if (date.getMinutes() === 0) {
    return format(date, "h a")
  } else {
    return format(date, "h:mm a")
  }
}

export function datesBetweenInc(dateStart?: Date | null, dateEnd?: Date | null) {
  if (!dateStart || !dateEnd) {
    return []
  }
  const dates = []
  let currentDate = dateStart
  while (currentDate <= dateEnd) {
    dates.push(currentDate)
    currentDate = add(currentDate, { days: 1 })
  }
  return dates
}

/**
 * Splits a date range into daily chunks, with each day's time range padded from 2 AM to 10 PM.
 * The first and last days are special cases:
 * - The first day starts at the given start time and ends at 10 PM or the end time if it's on the same day.
 * - The last day starts at 2 AM (or 1 AM if it's the immediate next day) and ends at the given end time.
 * This function is useful for dividing a time range into manageable daily segments,
 * while accommodating for specific start and end times outside the usual padding.
 *
 * @example
 * // Splits the range from March 1, 2024, 9:30 AM to March 3, 2024, 5:30 AM into daily chunks.
 * const start = "2024-03-01T09:30:00";
 * const end = "2024-03-03T05:30:00";
 * const chunks = splitDateRangeIntoChunks(start, end);
 * console.log(chunks);
 * // Output:
 * // [
 * //   { start: "2024-03-01T09:30:00.000Z", end: "2024-03-01T22:00:00.000Z" },
 * //   { start: "2024-03-02T02:00:00.000Z", end: "2024-03-02T22:00:00.000Z" },
 * //   { start: "2024-03-03T01:00:00.000Z", end: "2024-03-03T05:30:00.000Z" }
 * // ]
 */
export function splitDateRangeIntoChunks(start: Date | string, end: Date | string) {
  const startDate = new Date(start)
  const endDate = new Date(end)

  if (startDate > endDate) {
    return []
  }
  if (isSameDay(startDate, endDate)) {
    return [{ start: startDate, end: endDate }]
  }

  const days = datesBetweenInc(startDate, endDate)
  const chunks = days.map((day, index) => {
    const start = new Date(day)
    const end = new Date(day)

    if (index === 0) {
      start.setTime(startDate.getTime())
    } else {
      start.setHours(2, 0, 0, 0)
    }

    if (index === days.length - 1) {
      end.setTime(endDate.getTime())
    } else {
      end.setHours(22, 0, 0, 0)
    }

    return { start, end }
  })
  return chunks
}

// Returns true if the given date range spans the entire day. This works alongside the `splitDateRangeIntoChunks` function meaning a day is considered to be from 2 AM to 10 PM.
export function isAllDay(start: Date, end: Date) {
  if (!isSameDay(start, end)) {
    return false
  }
  return isStartOfDay(start) && isEndOfDay(end)
}

// Works similarly to the `isAllDay` function but considers the start date only.
export function isEndOfDay(date: Date) {
  return date.getHours() >= 22
}

// Works similarly to the `isAllDay` function but considers the end date only.
export function isStartOfDay(date: Date) {
  return date.getHours() <= 2
}

export function differenceInDaysWithoutTime(start: Date, end: Date) {
  const startWithoutTime = new Date(start)
  startWithoutTime.setHours(0, 0, 0, 0)
  const endWithoutTime = new Date(end)
  endWithoutTime.setHours(0, 0, 0, 0)
  return Math.round((endWithoutTime.getTime() - startWithoutTime.getTime()) / (1000 * 60 * 60 * 24))
}

export function minDate(date1: Date, date2: Date) {
  return date1 < date2 ? date1 : date2
}

export function maxDate(date1: Date, date2: Date) {
  return date1 > date2 ? date1 : date2
}

export function dateRangeIncludes(range: { start: Date; end: Date }, date: Date) {
  const start = new Date(range.start)
  const end = new Date(range.end)
  const target = new Date(date)
  return target >= start && target <= end
}

export function durationInMinutesWithoutOverlaps(
  shifts: { startDate: Date; endDate: Date; breakDuration: number }[],
): number {
  // Sort the shifts based on start date
  shifts.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())

  const mergedShifts: { startDate: Date; endDate: Date; breakDuration: number }[] = []

  for (const shift of shifts) {
    if (mergedShifts.length === 0) {
      mergedShifts.push(shift)
    } else {
      const lastShift = mergedShifts[mergedShifts.length - 1]
      if (lastShift.endDate >= shift.startDate) {
        // Merge shifts if they overlap
        lastShift.endDate = new Date(Math.max(lastShift.endDate.getTime(), shift.endDate.getTime()))
        // Adjust break duration accordingly (assuming breaks cannot overlap)
        lastShift.breakDuration += shift.breakDuration
      } else {
        mergedShifts.push(shift)
      }
    }
  }

  // Calculate total duration of merged shifts
  return mergedShifts.reduce(
    (total, shift) =>
      total + differenceInMinutes(shift.endDate, shift.startDate) - shift.breakDuration,
    0,
  )
}

export function tryParseUrlDateQuery(key: string): Date | null {
  const value = new URLSearchParams(window.location.search).get(key)
  if (!value) return null
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date
}

// yyyy-MM-dd__yyyy-MM-dd
export function serializeDateRange(range: { start: Date; end: Date }) {
  return `${formatDateForUrl(range.start)}__${formatDateForUrl(range.end)}`
}

export function unserializeDateRange(value: string): { start: Date; end: Date } | null {
  const [from, to] = value.split("__")
  const fromDate = new Date(from)
  const toDate = new Date(to)
  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
    return null
  }
  return { start: fromDate, end: toDate }
}

export function formatDateRange(startDate: Date | string, endDate: Date | string) {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()

  if (start.toDateString() === end.toDateString()) {
    return format(start, "EEEE, d MMM yyyy")
  }
  if (sameMonth) {
    return `${format(start, "d")} – ${format(end, "d MMM yyyy")}`
  }
  if (sameYear) {
    return `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`
  }
  return `${format(start, "d MMM yyyy")} – ${format(end, "d MMM yyyy")}`
}
